import React, { useEffect, useRef, useState } from 'react';

import { useBackendVersion } from '@/hooks/useSignage';
import {
  APP_VERSION,
  BUILD_TIME,
  GIT_SHA,
  isBuildMismatch,
} from '@/utils/buildInfo';

function formatIso(iso: string | undefined): string {
  if (!iso) return '—';
  const ts = new Date(iso);
  return Number.isNaN(ts.getTime()) ? iso : ts.toLocaleString();
}

/**
 * Small version indicator for the bottom of the sidebar: "v1.0.0 · abc1234".
 * Clicking it opens a popover with the frontend build time and the backend
 * version info from the public GET /api/v1/version endpoint (fetched once,
 * lazily on first open; renders "backend: ไม่ทราบ" when unavailable).
 *
 * The amber mismatch dot appears only when BOTH sides expose a real git sha
 * and they differ (monorepo: frontend and backend are built from the same
 * repo, so equal shas mean same release). Placeholder values ('dev',
 * 'unknown') suppress the dot entirely.
 */
export default function VersionBadge() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: backend, isError, isLoading } = useBackendVersion(open);

  const mismatch = isBuildMismatch(GIT_SHA, backend?.git_commit);
  const backendKnown =
    !isError && backend && (backend.version || backend.git_commit);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={containerRef} className='relative mt-2'>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        title='ข้อมูลเวอร์ชัน'
        className='flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1 font-mono text-[11px] text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600'
      >
        <span>
          v{APP_VERSION} · {GIT_SHA}
        </span>
        {mismatch && (
          <span
            data-testid='version-mismatch-dot'
            title='frontend/backend คนละ build'
            className='h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500'
          />
        )}
      </button>

      {open && (
        <div className='absolute bottom-full left-0 right-0 z-50 mb-2 rounded-md border border-gray-200 bg-white p-3 text-xs shadow-lg'>
          <p className='mb-1 font-semibold text-gray-700'>Frontend</p>
          <p className='text-gray-500'>
            v{APP_VERSION} · <span className='font-mono'>{GIT_SHA}</span>
          </p>
          <p className='text-gray-400'>build: {formatIso(BUILD_TIME)}</p>

          <p className='mb-1 mt-3 font-semibold text-gray-700'>Backend</p>
          {isLoading ? (
            <p className='text-gray-400'>กำลังโหลด…</p>
          ) : backendKnown ? (
            <>
              <p className='text-gray-500'>
                {backend.version || '—'}
                {backend.git_commit && (
                  <>
                    {' · '}
                    <span className='font-mono'>
                      {backend.git_commit.slice(0, 12)}
                    </span>
                  </>
                )}
              </p>
              <p className='text-gray-400'>
                build: {formatIso(backend.build_time)}
              </p>
              {backend.go_version && (
                <p className='text-gray-400'>{backend.go_version}</p>
              )}
              {mismatch && (
                <p className='mt-1 text-amber-600'>
                  frontend/backend คนละ build
                </p>
              )}
            </>
          ) : (
            <p className='text-gray-400'>backend: ไม่ทราบ</p>
          )}
        </div>
      )}
    </div>
  );
}
